import "leaflet/dist/leaflet.css";
import type { DashboardOverview } from "@/api/db/dashboard";
import { CircleMarker, MapContainer, TileLayer, Tooltip } from "react-leaflet";
import { DashboardLabels } from "@/lib/labels";

import {
	DashboardCard,
	DashboardCardBody,
	DashboardCardHeader,
	DashboardCardTitle,
} from "./dashboard-card";

const CENTER: [number, number] = [-19.93, -44.0];

function intensityColor(intensity: number) {
	return `color-mix(in srgb, var(--destructive) ${intensity * 100}%, var(--primary))`;
}

type City = DashboardOverview["cities"][number];

export function GeographicMap({
	cities,
	active = true,
}: {
	cities: DashboardOverview["cities"];
	active?: boolean;
}) {
	const mapped = cities.filter(
		(city): city is City & { coordinate: [number, number] } => city.coordinate !== null,
	);

	const maxTotal = Math.max(1, ...cities.map((city) => city.total));
	const ranking = cities.slice(0, 10);

	return (
		<DashboardCard data-component="dashboard-map">
			<DashboardCardHeader>
				<DashboardCardTitle>{DashboardLabels.map.title}</DashboardCardTitle>

				<span data-slot="mapped-count" className="font-mono text-xs text-muted-foreground">
					{mapped.length} cidades mapeadas
				</span>
			</DashboardCardHeader>

			<DashboardCardBody className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
				<div className="overflow-hidden rounded-md border border-border">
					{active && (
						<MapContainer
							center={CENTER}
							zoom={9}
							scrollWheelZoom={false}
							className="h-105 w-full bg-background"
						>
							<TileLayer
								attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
								url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
							/>

							{mapped.map((city) => {
								const intensity = city.total / maxTotal;

								return (
									<CircleMarker
										key={`${city.city}-${city.state ?? ""}`}
										center={city.coordinate}
										radius={6 + Math.sqrt(city.total) * 2.2}
										pathOptions={{
											color: "var(--primary-hover)",
											weight: 1,
											fillColor: intensityColor(intensity),
											fillOpacity: 0.55,
										}}
									>
										<Tooltip>
											<div className="flex flex-col font-sans">
												<strong>{city.city}</strong>
												{!!city.state && <span>{city.state}</span>}
												<span>
													{city.total} contato{city.total !== 1 && "s"} ERP
												</span>
											</div>
										</Tooltip>
									</CircleMarker>
								);
							})}
						</MapContainer>
					)}
					{!active && <div aria-hidden="true" className="h-105 bg-muted" />}
				</div>

				<div className="min-w-0">
					<p className="mb-2 font-display text-sm font-semibold text-foreground">
						{DashboardLabels.map.ranking}
					</p>

					<ol className="flex flex-col gap-2">
						{ranking.map((city, index) => (
							<li
								key={`${city.city}-${city.state ?? ""}`}
								data-component="city-rank"
								data-city={city.city}
								data-total={city.total}
								className="flex items-center gap-2"
							>
								<span className="w-4 shrink-0 font-mono text-xs text-muted-foreground">
									{index + 1}
								</span>

								<div className="min-w-0 flex-1">
									<div className="flex items-baseline justify-between gap-2">
										<span title={city.city} className="truncate text-sm text-foreground">
											{city.city}
										</span>
										<span className="shrink-0 font-mono text-xs text-muted-foreground">
											{city.total}
										</span>
									</div>

									<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-border">
										<div
											className="h-full rounded-full bg-primary"
											style={{ width: `${(city.total / maxTotal) * 100}%` }}
										/>
									</div>
								</div>
							</li>
						))}
					</ol>
				</div>
			</DashboardCardBody>
		</DashboardCard>
	);
}
